import React, { useCallback, useEffect, useState } from "react";
import { turnTakingAPI } from "../../services/api/turnTakingConfig";

const SCOPES = [
  { key: "normal", title: "Normal Calls" },
  { key: "stress", title: "Stress-Test Calls" },
];

const SMART_ENDPOINTING_OPTIONS = [
  // Three user-selectable modes. "smart" is not offered — it's the internal
  // fallback python_tools degrades "context" to on non-English calls. "native"
  // was removed (an A/B control that lost the matched comparison to unified).
  // [0] is the display fallback for an unknown/legacy stored value; "context"
  // (also the prod default) is the least-wrong thing to show for one.
  {
    value: "context",
    label: "Context-aware",
    help:
      "At the VAD gate, a text model (reads the words) and an audio model (reads the tone) both vote: the turn closes only if both agree the caller is done, and holds up to the budget below if either says they're mid-thought. English calls only; non-English falls back to an audio-only mode automatically.",
  },
  {
    value: "unified",
    label: "Unified",
    help:
      "VAD + Smart Turn (audio/prosody) + Deepgram only — no text model. Holds for a different amount of time depending on what Smart Turn says: patient (the 'mid-thought' ceiling below) when it actively says the caller isn't done, short (the 'no signal yet' ceiling) when there's no verdict yet. In a matched A/B against Context-aware it held the same latency with fewer barge-ins.",
  },
  {
    value: "off",
    label: "Endpoint Off",
    help:
      "Silence-only: ends the turn purely after the VAD gate. No semantic model, so the gate has to be long enough to ride out every pause — every turn pays that wait.",
  },
];

function TurnTakingSection({ scope, title, values, onChange, onSave, saving }) {
  const set = (key, value) => onChange(scope, { ...values, [key]: value });
  const mode = values.smart_endpointing;
  const activeOption =
    SMART_ENDPOINTING_OPTIONS.find((o) => o.value === mode) || SMART_ENDPOINTING_OPTIONS[0];
  const silenceLabel = "Hold budget when mid-thought (s)";

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <h2 className="text-sm font-medium text-foreground">{title}</h2>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
          Endpointing mode
        </label>
        {/* Segmented tabs — the active tab always reflects the saved value (no
            hidden dropdown state), so what you see is what will save. */}
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {SMART_ENDPOINTING_OPTIONS.map((opt) => {
            const active = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                onClick={() => set("smart_endpointing", opt.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  active
                    ? "bg-background text-foreground shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{activeOption.help}</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          VAD gate (s)
        </label>
        <input
          type="number"
          min="0"
          max="2"
          step="0.05"
          placeholder="default"
          disabled={mode === "flux"}
          value={values.vad_gate_secs ?? ""}
          onChange={(e) =>
            set(
              "vad_gate_secs",
              e.target.value === "" ? null : parseFloat(e.target.value)
            )
          }
          className="w-24 rounded border border-input px-2 py-1 text-sm disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          How much silence before the end-of-turn models are <em>asked</em> whether the
          caller is done. This is the latency knob: it is paid on every single turn.
          Leave blank to use the deployed default (0.5s). Too low fragments the caller
          mid-sentence; too high adds dead air to every reply.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Deepgram endpointing (ms)
        </label>
        <input
          type="number"
          min="0"
          max="2000"
          step="50"
          placeholder="default"
          disabled={mode === "flux"}
          value={values.endpointing_ms ?? ""}
          onChange={(e) =>
            set(
              "endpointing_ms",
              e.target.value === "" ? null : parseInt(e.target.value, 10)
            )
          }
          className="w-24 rounded border border-input px-2 py-1 text-sm disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          How much silence (ms) before Deepgram emits a final transcript. Blank =
          deployed default (100ms). Move it together with the VAD gate above — keep
          it comfortably below <code>VAD gate × 1000</code>. At 100ms a caller who
          reads from a script gets their sentence chopped into several separate
          transcript lines, and each fragment re-triggers turn detection.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          {silenceLabel}
        </label>
        <input
          type="number"
          min="0"
          max="3"
          step="0.05"
          disabled={mode === "flux" || mode === "unified"}
          value={values.end_of_turn_silence_secs}
          onChange={(e) => set("end_of_turn_silence_secs", parseFloat(e.target.value))}
          className="w-24 rounded border border-input px-2 py-1 text-sm disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          How long to keep waiting once a model says the turn is <em>not</em> over —
          the patience budget, not the gate. Only paid on turns that actually look
          unfinished, so raising it costs nothing on clean turns. Unified mode uses
          its own two ceilings below instead of this field.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Resume grace (s)
        </label>
        <input
          type="number"
          min="0"
          max="2"
          step="0.05"
          placeholder="default"
          disabled={mode !== "context" && mode !== "unified"}
          value={values.resume_grace_secs ?? ""}
          onChange={(e) =>
            set(
              "resume_grace_secs",
              e.target.value === "" ? null : parseFloat(e.target.value)
            )
          }
          className="w-24 rounded border border-input px-2 py-1 text-sm disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Context and Unified modes only. Once the models agree the turn is done, wait
          this long for the speaker to start again before replying — a complete
          sentence is not a complete turn. Catches the caller talking over an agent who
          paused mid-thought. Paid once per finished turn. Blank = deployed default
          (0.6s); 0 = reply immediately.
        </p>
      </div>

      {mode === "unified" && (
        <>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Unified ceiling: mid-thought (s)
            </label>
            <input
              type="number"
              min="0"
              max="5"
              step="0.05"
              placeholder="default"
              value={values.unified_ceiling_incomplete_secs ?? ""}
              onChange={(e) =>
                set(
                  "unified_ceiling_incomplete_secs",
                  e.target.value === "" ? null : parseFloat(e.target.value)
                )
              }
              className="w-24 rounded border border-input px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Unified mode only. How long to keep waiting once a model actively says
              the caller sounds mid-thought — the patient ceiling. Blank = deployed
              default.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Unified ceiling: no signal yet (s)
            </label>
            <input
              type="number"
              min="0"
              max="5"
              step="0.05"
              placeholder="default"
              value={values.unified_ceiling_no_signal_secs ?? ""}
              onChange={(e) =>
                set(
                  "unified_ceiling_no_signal_secs",
                  e.target.value === "" ? null : parseFloat(e.target.value)
                )
              }
              className="w-24 rounded border border-input px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Unified mode only. How long to keep waiting when there is simply no
              transcript yet to judge — shorter than the mid-thought ceiling above,
              since there's no positive signal the caller is still talking. Blank =
              deployed default.
            </p>
          </div>
        </>
      )}

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Fallback grace / stop timeout (s)
        </label>
        <input
          type="number"
          min="0"
          max="2"
          step="0.05"
          disabled={mode === "context" || mode === "unified" || mode === "native"}
          value={values.stop_timeout_secs}
          onChange={(e) => set("stop_timeout_secs", parseFloat(e.target.value))}
          className="w-24 rounded border border-input px-2 py-1 text-sm disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Smart and Off modes only. After the silence gap, how long to wait for the
          smart-turn model to confirm before closing an unsure turn (e.g. a trailing
          question). Lower = snappier; too low can clip the caller. Context, Unified
          and Native each compute their own fallback and ignore this field.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Interruption: min words
        </label>
        <input
          type="number"
          min="0"
          max="10"
          step="1"
          value={values.interruption_min_words}
          onChange={(e) => set("interruption_min_words", parseInt(e.target.value, 10))}
          className="w-24 rounded border border-input px-2 py-1 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Interruption: min voice seconds
        </label>
        <input
          type="number"
          min="0"
          max="1"
          step="0.05"
          value={values.interruption_min_voice_secs}
          onChange={(e) => set("interruption_min_voice_secs", parseFloat(e.target.value))}
          className="w-24 rounded border border-input px-2 py-1 text-sm"
        />
      </div>

      <button
        onClick={() => onSave(scope)}
        disabled={saving}
        className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

export default function AdminTurnTaking() {
  const [config, setConfig] = useState({ normal: null, stress: null });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState({});

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await turnTakingAPI.get();
      setConfig({ normal: data.normal, stress: data.stress });
    } catch (e) {
      showMsg("error", "Failed to load turn-taking config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (scope, values) => {
    setConfig((c) => ({ ...c, [scope]: values }));
  };

  const handleSave = async (scope) => {
    const cfg = config[scope];

    // Validate all numeric fields
    const endOfTurnSilence = cfg.end_of_turn_silence_secs;
    if (!Number.isFinite(endOfTurnSilence) || endOfTurnSilence < 0 || endOfTurnSilence > 3) {
      showMsg("error", "End-of-turn silence must be a number between 0 and 3");
      return;
    }

    const interruptionMinWords = cfg.interruption_min_words;
    if (!Number.isInteger(interruptionMinWords) || interruptionMinWords < 0 || interruptionMinWords > 10) {
      showMsg("error", "Interruption min words must be an integer between 0 and 10");
      return;
    }

    const interruptionMinVoiceSecs = cfg.interruption_min_voice_secs;
    if (!Number.isFinite(interruptionMinVoiceSecs) || interruptionMinVoiceSecs < 0 || interruptionMinVoiceSecs > 1) {
      showMsg("error", "Interruption min voice seconds must be a number between 0 and 1");
      return;
    }

    const stopTimeout = cfg.stop_timeout_secs;
    if (!Number.isFinite(stopTimeout) || stopTimeout < 0 || stopTimeout > 2) {
      showMsg("error", "Stop timeout must be a number between 0 and 2");
      return;
    }

    // null/undefined is valid — it means "inherit the deployed per-mode default".
    const vadGate = cfg.vad_gate_secs;
    if (vadGate !== null && vadGate !== undefined && vadGate !== "") {
      if (!Number.isFinite(vadGate) || vadGate < 0 || vadGate > 2) {
        showMsg("error", "VAD gate must be a number between 0 and 2, or blank for the default");
        return;
      }
    }

    // null/undefined is valid — "inherit the agent/profile default (100ms)".
    const endpointingMs = cfg.endpointing_ms;
    if (endpointingMs !== null && endpointingMs !== undefined && endpointingMs !== "") {
      if (!Number.isInteger(endpointingMs) || endpointingMs < 0 || endpointingMs > 2000) {
        showMsg("error", "Deepgram endpointing must be an integer 0–2000 ms, or blank for the default");
        return;
      }
    }

    // null/undefined is valid — "inherit the deployed default (0.6s)".
    const resumeGrace = cfg.resume_grace_secs;
    if (resumeGrace !== null && resumeGrace !== undefined && resumeGrace !== "") {
      if (!Number.isFinite(resumeGrace) || resumeGrace < 0 || resumeGrace > 2) {
        showMsg("error", "Resume grace must be a number between 0 and 2, or blank for the default");
        return;
      }
    }

    // null/undefined is valid — "inherit the deployed default" for both ceilings.
    const ceilingIncomplete = cfg.unified_ceiling_incomplete_secs;
    if (ceilingIncomplete !== null && ceilingIncomplete !== undefined && ceilingIncomplete !== "") {
      if (!Number.isFinite(ceilingIncomplete) || ceilingIncomplete < 0 || ceilingIncomplete > 5) {
        showMsg("error", "Unified mid-thought ceiling must be a number between 0 and 5, or blank for the default");
        return;
      }
    }

    const ceilingNoSignal = cfg.unified_ceiling_no_signal_secs;
    if (ceilingNoSignal !== null && ceilingNoSignal !== undefined && ceilingNoSignal !== "") {
      if (!Number.isFinite(ceilingNoSignal) || ceilingNoSignal < 0 || ceilingNoSignal > 5) {
        showMsg("error", "Unified no-signal ceiling must be a number between 0 and 5, or blank for the default");
        return;
      }
    }

    // Validate smart_endpointing. "smart" (internal non-English fallback) and
    // "flux" (legacy) are accepted-if-stored but not offered in the picker;
    // "native" was removed.
    if (!["off", "smart", "context", "unified", "flux"].includes(cfg.smart_endpointing)) {
      showMsg("error", "Smart endpointing must be one of: off, context, unified");
      return;
    }

    setSaving((s) => ({ ...s, [scope]: true }));
    try {
      await turnTakingAPI.update(scope, cfg);
      showMsg("success", `${scope} turn-taking config saved`);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to save turn-taking config");
    } finally {
      setSaving((s) => ({ ...s, [scope]: false }));
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading turn-taking config…</div>;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-foreground">Turn-Taking</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          End-of-turn silence and interruption thresholds, per call type. Changes apply on the next
          call — no deploy, no restart.
        </p>
      </div>

      {msg.text && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            msg.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SCOPES.map(({ key, title }) =>
          config[key] ? (
            <TurnTakingSection
              key={key}
              scope={key}
              title={title}
              values={config[key]}
              onChange={handleChange}
              onSave={handleSave}
              saving={!!saving[key]}
            />
          ) : null
        )}
      </div>
    </div>
  );
}
